// A régua visual do Pipeline — o que os CRMs de referência mostram em cada
// negócio e o nosso não mostrava.
//
// Pedido do Lucas (01/10/2026): *"Avalie o padrão ouro de CRM do mercado, em
// especial a visualização do funil de vendas e implemente no nosso pipeline.
// […] É um trabalho de UI, não de lógica do negócio."*
//
// O que Pipedrive, HubSpot e Salesforce têm em comum, e que aqui vira função:
//
//  1. **Negócio parado apodrece à vista** ("rotting" no Pipedrive): o cartão
//     muda de cor conforme os dias sem movimento. Aqui → `diasSemMovimento` +
//     `nivelParado`.
//  2. **Todo negócio aberto tem um próximo passo — e o que não tem, grita**
//     (o triângulo amarelo do Pipedrive). Aqui → `situacaoDoProximoPasso`.
//  3. **O funil é um desenho, com quantidade e valor por etapa**, não uma lista
//     de etiquetas. Aqui → `resumoDoFunil`.
//  4. **A coluna do quadro se ordena** (valor, parados primeiro, recentes).
//     Aqui → `ordenarNegocios`.
//  5. **A etapa atual diz há quanto tempo o negócio está nela** (o "Path" do
//     Salesforce). Aqui → `desdeQuandoNaEtapa`, lida do histórico que já existe.
//
// Medido em produção em 01/10/2026 (somente leitura), e é o que calibra os
// limites — régua que pinta tudo de vermelho não diz nada:
//
//  • 181 negócios abertos, **169 deles na etapa Follow-up** (R$ 22,8 mi).
//  • Dias sem movimento: 27 negócios até 30 dias, 35 entre 31 e 90, **119 acima
//    de 90**. Daí os dois cortes — 30 e 90 — que separam três grupos de verdade.
//  • **178 dos 181 abertos não têm nenhuma atividade agendada.**
//  • 55 abertos sem responsável.
//
// Nada aqui grava nem decide regra de negócio: tudo é leitura do que
// `GET /comercial/propostas` já devolve (`p.*`, inclusive `atualizado_em`).
// A régua é pura de propósito — o teste roda no CI (job dre-frontend), onde o
// jest do CRA5 não consegue carregar o antd v6.

/** O que a régua precisa de um negócio. O resto da linha não interessa. */
export type NegocioSaude = {
  id?: number;
  status: string;
  valor_estimado?: number | string | null;
  valor_final?: number | string | null;
  probabilidade?: number | string | null;
  responsavel?: string | null;
  data_abertura?: string | null;
  criado_em?: string | null;
  atualizado_em?: string | null;
  origem?: string | null;
};

/**
 * O resumo de atividades em aberto que `com_resumo_followups_negocios` devolve.
 * `proximo` é a data (AAAA-MM-DD) da mais próxima, ou null quando nenhuma tem
 * data. `vencido`/`hoje` vêm do servidor contados em UTC — a tela NÃO os usa:
 * decide pelo dia local a partir de `proximo` (depois das 21h de Brasília o
 * "hoje" do servidor já é amanhã, e a atividade de hoje aparecia atrasada).
 */
export type ProximoPasso = { total: number; proximo: string | null; vencido?: boolean; hoje?: boolean };

/** O dia de hoje no relógio de quem está olhando a tela, AAAA-MM-DD. */
export function hojeLocalIso(agora: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${agora.getFullYear()}-${p(agora.getMonth() + 1)}-${p(agora.getDate())}`;
}

export const ETAPAS_ABERTAS = ['prospeccao', 'qualificacao', 'proposta', 'follow_up', 'negociacao'];
export const ETAPAS_FECHADAS = ['ganho', 'perdido', 'cancelado'];

export const ehAberto = (status: string) => !ETAPAS_FECHADAS.includes(status);

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v == null ? '' : v));
  return Number.isFinite(n) ? n : 0;
};

/**
 * O valor que representa o negócio. Ganho vale o que foi VENDIDO (`valor_final`)
 * — é a régua do cartão "Contratado no Ano"; aberto vale a estimativa.
 */
export function valorDoNegocio(p: NegocioSaude): number {
  if (p.status === 'ganho' && num(p.valor_final) > 0) return num(p.valor_final);
  return num(p.valor_estimado);
}

/** Valor × probabilidade. Só faz sentido para negócio aberto. */
/**
 * Valor em forma curta, para cabeçalho: "R$ 22,8 mi", "R$ 97 mil", "R$ 850".
 * Montado à mão (sem `toLocaleString`) para dar o mesmo texto em qualquer
 * navegador e no jest. O valor exato fica sempre na dica de quem usa.
 */
export function valorCompacto(v: number | string | null | undefined): string {
  const n = Math.abs(Number(v) || 0);
  const virgula = (x: number) => x.toFixed(1).replace('.', ',').replace(/,0$/, '');
  // 999.500 já arredonda para "1.000 mil": dali em diante fala em milhão.
  if (n >= 999500) return `R$ ${virgula(n / 1e6)} mi`;
  if (n >= 1000) return `R$ ${Math.round(n / 1000)} mil`;
  return `R$ ${Math.round(n)}`;
}

export function valorPonderado(p: NegocioSaude): number {
  if (!ehAberto(p.status)) return 0;
  return num(p.valor_estimado) * (num(p.probabilidade) / 100);
}

// ── 1. Negócio parado ────────────────────────────────────────────────────

/** Até aqui o negócio está em dia. */
export const LIMITE_ATENCAO = 30;
/** Acima disto o negócio está parado. */
export const LIMITE_PARADO = 90;

const MS_DIA = 86400000;

/** Meio-dia local do dia de uma data — conta dias sem tropeçar em fuso. */
function meioDia(v: string | Date): number {
  if (v instanceof Date) {
    return new Date(v.getFullYear(), v.getMonth(), v.getDate(), 12).getTime();
  }
  const s = String(v);
  // "2026-09-30" ou "2026-09-30T14:05:00": o DIA é o que está escrito.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], 12).getTime();
  const d = new Date(s);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12).getTime();
}

/**
 * Dias desde a última vez que alguém mexeu no negócio (`atualizado_em`, que o
 * servidor carimba em toda gravação). Sem ele, vale a criação; sem nada, null.
 */
export function diasSemMovimento(p: NegocioSaude, agora: Date = new Date()): number | null {
  const ref = p.atualizado_em || p.criado_em || p.data_abertura;
  if (!ref) return null;
  const t = meioDia(ref);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((meioDia(agora) - t) / MS_DIA));
}

export type NivelParado = 'em_dia' | 'atencao' | 'parado' | 'fechado';

/** Negócio fechado não apodrece — não há o que cobrar de quem ganhou ou perdeu. */
export function nivelParado(p: NegocioSaude, agora: Date = new Date()): NivelParado {
  if (!ehAberto(p.status)) return 'fechado';
  const d = diasSemMovimento(p, agora);
  if (d == null || d <= LIMITE_ATENCAO) return 'em_dia';
  return d <= LIMITE_PARADO ? 'atencao' : 'parado';
}

export const COR_DO_NIVEL: Record<NivelParado, string> = {
  em_dia: '#0a7f4a', atencao: '#B07500', parado: '#B91626', fechado: '#9AA0B4',
};

/** "hoje", "há 1 dia", "há 121 dias" — frase para gente, não número solto. */
export function textoDeDias(d: number | null): string {
  if (d == null) return 'sem data';
  if (d === 0) return 'hoje';
  return d === 1 ? 'há 1 dia' : `há ${d} dias`;
}

// ── 2. Próximo passo ─────────────────────────────────────────────────────

export type SituacaoPasso = 'vencido' | 'hoje' | 'agendado' | 'nenhum' | 'nao_se_aplica';

export function situacaoDoProximoPasso(
  p: NegocioSaude, passo?: ProximoPasso | null, agora: Date = new Date(),
): SituacaoPasso {
  if (!ehAberto(p.status)) return 'nao_se_aplica';
  if (!passo) return 'nenhum';
  const dia = String(passo.proximo || '').slice(0, 10);
  if (!dia) return 'agendado';             // há atividade em aberto, só não tem data
  const hoje = hojeLocalIso(agora);
  if (dia < hoje) return 'vencido';
  return dia === hoje ? 'hoje' : 'agendado';
}

export const COR_DO_PASSO: Record<SituacaoPasso, string> = {
  vencido: '#B91626', hoje: '#B07500', agendado: '#0930D0', nenhum: '#9AA0B4', nao_se_aplica: '#9AA0B4',
};

/** "30/09" a partir de "2026-09-30" — sem passar por Date (fuso). */
export function diaMes(iso?: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}` : '';
}

export function textoDoPasso(s: SituacaoPasso, passo?: ProximoPasso | null): string {
  if (s === 'nenhum') return 'Sem próximo passo';
  if (s === 'nao_se_aplica' || !passo) return '';
  if (s === 'vencido') return `Atrasado desde ${diaMes(passo.proximo)}`;
  if (s === 'hoje') return 'Para hoje';
  return passo.proximo ? `Agendado para ${diaMes(passo.proximo)}` : 'Em aberto, sem data';
}

// ── 3. O funil ───────────────────────────────────────────────────────────

export type EtapaDoFunil = {
  status: string;
  qtd: number;
  valor: number;
  ponderado: number;
  /** Quantos estão parados (acima de LIMITE_PARADO). */
  parados: number;
  /** Fatia do valor aberto que esta etapa carrega, 0–1. */
  fatia: number;
};

export type ResumoFunil = {
  etapas: EtapaDoFunil[];            // as abertas, na ordem do funil
  ganho: EtapaDoFunil;
  perdido: EtapaDoFunil;
  abertos: { qtd: number; valor: number; ponderado: number };
};

export function resumoDoFunil(negocios: NegocioSaude[], agora: Date = new Date()): ResumoFunil {
  const vazia = (status: string): EtapaDoFunil =>
    ({ status, qtd: 0, valor: 0, ponderado: 0, parados: 0, fatia: 0 });
  const por: Record<string, EtapaDoFunil> = {};
  for (const s of [...ETAPAS_ABERTAS, 'ganho', 'perdido']) por[s] = vazia(s);
  for (const p of negocios || []) {
    const e = por[p.status];
    if (!e) continue;                 // cancelado e desconhecidos ficam fora do desenho
    e.qtd++;
    e.valor += valorDoNegocio(p);
    e.ponderado += valorPonderado(p);
    if (nivelParado(p, agora) === 'parado') e.parados++;
  }
  const etapas = ETAPAS_ABERTAS.map(s => por[s]);
  const abertos = etapas.reduce((a, e) => ({
    qtd: a.qtd + e.qtd, valor: a.valor + e.valor, ponderado: a.ponderado + e.ponderado,
  }), { qtd: 0, valor: 0, ponderado: 0 });
  for (const e of etapas) e.fatia = abertos.valor > 0 ? e.valor / abertos.valor : 0;
  return { etapas, ganho: por.ganho, perdido: por.perdido, abertos };
}

// ── 4. Ordenação ─────────────────────────────────────────────────────────

export type Ordenacao = 'valor' | 'parados' | 'recentes';

export function ordenarNegocios<T extends NegocioSaude>(
  lista: T[], criterio: Ordenacao, agora: Date = new Date(),
): T[] {
  const copia = (lista || []).slice();
  const idle = (p: T) => { const d = diasSemMovimento(p, agora); return d == null ? -1 : d; };
  if (criterio === 'parados') {
    // Parado há mais tempo primeiro; empate decide pelo maior valor.
    return copia.sort((a, b) => idle(b) - idle(a) || valorDoNegocio(b) - valorDoNegocio(a));
  }
  if (criterio === 'recentes') {
    return copia.sort((a, b) => idle(a) - idle(b) || valorDoNegocio(b) - valorDoNegocio(a));
  }
  return copia.sort((a, b) => valorDoNegocio(b) - valorDoNegocio(a));
}

// ── Sinais rápidos (os atalhos de filtro acima do quadro) ────────────────

export type Sinal = '' | 'sem_passo' | 'parado' | 'sem_responsavel';

export function casaSinal(
  p: NegocioSaude, sinal: Sinal, passo?: ProximoPasso | null, agora: Date = new Date(),
): boolean {
  if (!sinal) return true;
  if (!ehAberto(p.status)) return false;      // sinal é cobrança, e só se cobra o que está aberto
  if (sinal === 'sem_passo') return !passo;
  if (sinal === 'parado') return nivelParado(p, agora) === 'parado';
  return !String(p.responsavel || '').trim();
}

export function contarSinais(
  negocios: NegocioSaude[], passos: Record<number, ProximoPasso>, agora: Date = new Date(),
): { abertos: number; sem_passo: number; parado: number; sem_responsavel: number } {
  const r = { abertos: 0, sem_passo: 0, parado: 0, sem_responsavel: 0 };
  for (const p of negocios || []) {
    if (!ehAberto(p.status)) continue;
    r.abertos++;
    const passo = p.id != null ? passos[p.id] : null;
    if (!passo) r.sem_passo++;
    if (nivelParado(p, agora) === 'parado') r.parado++;
    if (!String(p.responsavel || '').trim()) r.sem_responsavel++;
  }
  return r;
}

// ── Dono do negócio (avatar) ─────────────────────────────────────────────

/** "Gustavo Schefelbanis Araujo" → "GA" (primeiro e último nome). */
export function iniciais(nome?: string | null): string {
  const partes = String(nome || '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

const CORES_DONO = ['#0930D0', '#0a7f4a', '#7C3AED', '#B07500', '#017C9A', '#B91626', '#4A6285'];

/** Cor estável por pessoa: a mesma pessoa tem a mesma cor em toda tela. */
export function corDoDono(nome?: string | null): string {
  const s = String(nome || '').trim().toLowerCase();
  if (!s) return '#9AA0B4';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 9973;
  return CORES_DONO[h % CORES_DONO.length];
}

// ── 5. Há quanto tempo na etapa ──────────────────────────────────────────

export type EventoDeStatus = { tipo?: string; criado_em?: string; meta?: any };

/**
 * A data em que o negócio ENTROU na etapa atual: o `status_alterado` mais
 * recente cujo destino (`meta.para`) é o status de agora. Sem evento — negócio
 * que nunca mudou de etapa —, vale a criação.
 */
export function desdeQuandoNaEtapa(
  statusAtual: string, eventos: EventoDeStatus[], criadoEm?: string | null,
): string | null {
  let melhor: string | null = null;
  for (const ev of eventos || []) {
    if (ev.tipo !== 'status_alterado' || !ev.criado_em) continue;
    let meta = ev.meta;
    if (typeof meta === 'string') { try { meta = JSON.parse(meta); } catch { meta = null; } }
    if (!meta || meta.para !== statusAtual) continue;
    if (!melhor || new Date(ev.criado_em).getTime() > new Date(melhor).getTime()) melhor = ev.criado_em;
  }
  return melhor || criadoEm || null;
}

/** Dias corridos desde uma data até agora (0 = hoje). */
export function diasDesdeData(iso: string | null | undefined, agora: Date = new Date()): number | null {
  if (!iso) return null;
  const t = meioDia(iso);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((meioDia(agora) - t) / MS_DIA));
}
